<template>
    <div class="guest-nav-bar absolute w-full bg-white border-gray-200 border-opacity-10 top-0 z-[10001]">
        <div class="max-w-[1280px] mx-auto h-16 flex items-center justify-between">
            <div class="flex items-center gap-6">
                <!-- Logo -->
                <a href="/home" class="flex items-center gap-2">
                    <PtIcon :icon="Logo" class="w-[135px] h-[35px]"/>
                </a>

                <!-- Navigation Links -->
                <nav class="hidden md:flex items-center gap-8">
                    <button class="bg-gray-100 hover:text-gray-900 transition-colors th-btn th-btn-active th-btn-plain pointer-events-none">{{ t('NavBar.R&D') }}</button>
                    <a href="#toolkit" class="text-gray-600 hover:text-gray-900 transition-colors th-btn th-btn-active th-btn-plain" :class="{ 'bg-gray-100': hash === '#toolkit' }">{{ t('NavBar.features') }}</a>
                    <a href="#pricing" class="text-gray-600 hover:text-gray-900 transition-colors th-btn th-btn-active th-btn-plain" :class="{ 'bg-gray-100': hash === '#pricing' }">{{ t('NavBar.pricing') }}</a>
                </nav>
            </div>
            <!-- Right Actions -->
            <div class="flex items-center gap-4">
                <!-- Language Selector -->
                <div class="relative">
                    <button
                        @click.stop="showLanguageDropdown = !showLanguageDropdown"
                        class="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 transition-colors"
                    >
                        <span>{{ currentLanguage }}</span>
                        <PtIcon icon="SolidArrowsmallDown" class="w-4 h-4 text-gray-600 fill-gray-600" />
                    </button>

                    <!-- Language Dropdown -->
                    <div
                        v-if="showLanguageDropdown"
                        class="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
                        @click.stop
                    >
                        <button
                            @click="changeLanguage('en')"
                            :class="[
                                'w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center justify-between',
                                i18n.global.locale.value === 'en' ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                            ]"
                        >
                            <span>English</span>
                            <PtIcon icon="SolidTick" class="w-4 h-4 text-eureka-500 fill-eureka-500" v-if="i18n.global.locale.value === 'en'" />
                        </button>
                        <button
                            @click="changeLanguage('cn')"
                            :class="[
                                'w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center justify-between',
                                i18n.global.locale.value === 'cn' ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                            ]"
                        >
                            <span>简体中文</span>
                            <PtIcon icon="SolidTick" class="w-4 h-4 text-eureka-500 fill-eureka-500" v-if="i18n.global.locale.value === 'cn'" />
                        </button>
                        <button
                            @click="changeLanguage('tw')"
                            :class="[
                                'w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center justify-between',
                                i18n.global.locale.value === 'tw' ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                            ]"
                        >
                            <span>繁體中文</span>
                            <PtIcon icon="SolidTick" class="w-4 h-4 text-eureka-500 fill-eureka-500" v-if="i18n.global.locale.value === 'tw'" />
                        </button>
                        <button
                            @click="changeLanguage('jp')"
                            :class="[
                                'w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center justify-between',
                                i18n.global.locale.value === 'jp' ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                            ]"
                        >
                            <span>日本語</span>
                            <PtIcon icon="SolidTick" class="w-4 h-4 text-eureka-500 fill-eureka-500" v-if="i18n.global.locale.value === 'jp'" />
                        </button>
                    </div>
                </div>

                <!-- Get Started Button -->
                <button
                    @click="handleGetStarted"
                    class="flex items-center gap-2 px-6 py-2.5 bg-eureka-600 hover:bg-eureka-700 text-white rounded-lg transition-colors font-medium"
                >
                    <PtIcon icon="SolidRocket" class="w-5 h-5 fill-white" />
                    <span>{{ t('NavBar.getStarted') }}</span>
                </button>

                <!-- Mobile Menu Button -->
                <button
                    @click="showMobileMenu = !showMobileMenu"
                    class="md:hidden p-2"
                    aria-label="Toggle mobile menu"
                >
                    <PtIcon icon="SolidMenu4" class="w-6 h-6 text-gray-600 fill-gray-600" />
                </button>
            </div>
        </div>

        <!-- Mobile Menu -->
        <div v-if="showMobileMenu" class="md:hidden border-t border-gray-200 bg-white">
            <nav class="px-6 py-4 flex flex-col gap-4" role="navigation" aria-label="Mobile navigation">
                <a href="/r-and-d" class="text-gray-600 hover:text-gray-900 py-2">{{ t('NavBar.R&D') }}</a>
                <a href="/features" class="text-gray-600 hover:text-gray-900 py-2">{{ t('NavBar.features') }}</a>
                <a href="/pricing" class="text-gray-600 hover:text-gray-900 py-2">{{ t('NavBar.pricing') }}</a>
            </nav>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { PtIcon } from 'patsnap-biz';
import { type HeaderService } from '@patsnap-utils/biz';
import { loginSDK } from '@eureka/shared/login-sdk';
import Logo from '../assets/eureka-logo-color.svg';
import { useI18n } from 'vue-i18n';
import { i18n } from '@/infrastructure/locales';
import { cn, en, jp, tw } from '../infrastructure/locales';

import '@patsnap-ui/icon/assets/solid/Menu4.svg';
import { useRoute } from 'vue-router';

// Merge nav-bar translations into global i18n
i18n.global.mergeLocaleMessage('cn', cn);
i18n.global.mergeLocaleMessage('en', en);
i18n.global.mergeLocaleMessage('jp', jp);
i18n.global.mergeLocaleMessage('tw', tw);

const route = useRoute();
const { t } = useI18n();

const props = defineProps<{
    headerService: HeaderService;
    login?: () => void;
}>();

const showLanguageDropdown = ref(false);
const showMobileMenu = ref(false);

const languageMap = {
    en: 'English',
    cn: '简体中文',
    tw: '繁體中文',
    jp: '日本語',
};

const currentLanguage = computed(() => {
    // Use i18n.global.locale to make it reactive
    const lang = i18n.global.locale.value;
    return languageMap[lang as keyof typeof languageMap] || 'Language';
});

function handleGetStarted() {
    if (props.login) {
        props.login();
        return;
    }
    loginSDK.open().then(() => {
        window.location.reload();
    });
}

function changeLanguage(lang: string) {
    showLanguageDropdown.value = false;

    // Update i18n locale without page reload
    i18n.global.locale.value = lang as 'en' | 'cn' | 'jp' | 'tw';

    // Set cookie to persist language preference
    // document.cookie = `---uniform-setting-locale=${lang}; path=/; max-age=31536000`;

    // Call headerService if available (for any additional side effects)
    if (props.headerService.beforeLocaleChange) {
        props.headerService.beforeLocaleChange(lang, () => {
            window.location.reload();
        });
    }
}

const hash = computed(() => route.hash);

onMounted(() => {
    // Close dropdowns when clicking outside
    document.addEventListener('click', () => {
        showLanguageDropdown.value = false;
    });
});
</script>

<style lang="scss" scoped>
.guest-nav-bar {
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}
</style>
